import {CustomerAddress} from '~/composables';
import {TransformedCustomerAddress} from '~/modules/customer/composables/types';
import {transformUserGetter} from '~/modules/customer/helpers/userAddressManipulator';

export interface UserAddressesGetters<GENERIC_USER_ADDRESS_ITEM, SPECIFIC_USER_ADDRESS_ITEM> {
  getAddresses: (shipping: GENERIC_USER_ADDRESS_ITEM[], criteria?: Record<string, any>) => GENERIC_USER_ADDRESS_ITEM[];
  getTotal: (shipping: GENERIC_USER_ADDRESS_ITEM[]) => number;
  getPostCode: (address: GENERIC_USER_ADDRESS_ITEM) => string;
  getStreetName: (address: GENERIC_USER_ADDRESS_ITEM) => string;
  getCity: (address: GENERIC_USER_ADDRESS_ITEM) => string;
  getFirstName: (address: GENERIC_USER_ADDRESS_ITEM) => string;
  getAddressName: (address: GENERIC_USER_ADDRESS_ITEM) => string;
  getAddressGender: (address: GENERIC_USER_ADDRESS_ITEM) => number;
  getLastName: (address: GENERIC_USER_ADDRESS_ITEM) => string;
  getCountry: (address: GENERIC_USER_ADDRESS_ITEM) => string;
  getPhone: (address: GENERIC_USER_ADDRESS_ITEM) => string;
  getEmail: (address: SPECIFIC_USER_ADDRESS_ITEM) => string;
  getApartmentNumber: (address: SPECIFIC_USER_ADDRESS_ITEM) => string | number;
  getProvince: (address: GENERIC_USER_ADDRESS_ITEM) => string;
  getCompanyName: (address: GENERIC_USER_ADDRESS_ITEM) => string;
  getTaxNumber: (address: GENERIC_USER_ADDRESS_ITEM) => string;
  getId: (address: GENERIC_USER_ADDRESS_ITEM) => string | number;
  isDefault: (address: GENERIC_USER_ADDRESS_ITEM) => boolean;
  isDefaultShipping: (address: GENERIC_USER_ADDRESS_ITEM) => boolean;
  isDefaultBilling: (address: GENERIC_USER_ADDRESS_ITEM) => boolean;
  getNeighborhood: (address: any) => string
  getAddressExtra: (address: any) => string
}

const userAddressesGetters: UserAddressesGetters<CustomerAddress, TransformedCustomerAddress> = {
  getAddresses: (addresses, criteria?: Record<string, any>) => {
    if (!addresses || !Array.isArray(addresses) || addresses.length === 0) {
      return [];
    }

    const uniqueAddresses = new Map();

    addresses.forEach((address) => {
      const transformedAddress = transformUserGetter(address);
      const addressSignature = Object.keys(transformedAddress)
        .filter((key) => key !== 'id' && key !== '__typename' && key !== 'default_billing' && key !== 'default_shipping')
        .sort()
        .map((key) => `${key}:${transformedAddress[key]}`)
        .join('|');

      if (!uniqueAddresses.has(addressSignature)) {
        uniqueAddresses.set(addressSignature, transformedAddress);
      }
    });

    return Array.from(uniqueAddresses.values());
  },

  getTotal: (addresses) => addresses.length,

  getPostCode: (address) => address?.postcode || '',
  getStreetName: (address) => (Array.isArray(address?.street) ? address?.street[0] : address?.street),
  getCity: (address) => address?.city || '',
  getFirstName: (address) => address?.firstname || '',
  getAddressName: (address) => address?.address_name || '',
  getAddressGender: (address) => address?.address_gender || 0,
  getLastName: (address) => address?.lastname || '',
  getCountry: (address) => address?.country_code || '',
  getPhone: (address) => address?.telephone || '',
  getEmail: (address) => address?.email || '',
  getApartmentNumber: (address) => (Array.isArray(address?.street) ? address?.street[1] : address?.apartment),
  getProvince: (address) => (address?.region?.region || address?.region?.region_code) || '',
  getCompanyName: (address) => address?.company || '',
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getTaxNumber: (address) => address?.vat_id || '',
  getId: (address) => address?.id || '',
  getNeighborhood: (address) => (Array.isArray(address?.street) ? address?.street[2] : address?.neighborhood),
  getAddressExtra: (address) => (Array.isArray(address?.street) ? address?.street[3] : address?.extra),
  isDefault: (address) => (address?.default_shipping || address?.default_billing) || false,
  isDefaultShipping: (address) => address?.default_shipping || false,
  isDefaultBilling: (address) => address?.default_billing || false,
};

export default userAddressesGetters;
